import React, { useState, useEffect, useRef } from 'react'
import {
  Plus,
  Download,
  MapPin,
  CheckCircle,
  XCircle
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import assetLocationMasterService from '../../services/assetLocationMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const AssetLocationMaster = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalLocations: 0,
    activeLocations: 0,
    inactiveLocations: 0,
    byType: {}
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [listResponse, statsResponse] = await Promise.all([
        assetLocationMasterService.getAllLocations({ limit: 1000 }),
        assetLocationMasterService.getLocationStats()
      ])

      if (listResponse.success) {
        // Flatten the address so the table can search and sort on city/country
        const transformedData = (listResponse.data || []).map(item => ({
          ...item,
          id: item._id,
          city: item.address?.city || '',
          country: item.address?.country || ''
        }))
        setData(transformedData)
        if (listResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No asset location records found. Create your first location!')
        }
      } else {
        toast.error(listResponse.message || 'Failed to load asset locations')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Error loading asset location records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view asset locations')
      } else if (!error._errorHandled) {
        toast.error('Failed to load asset location records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'locationCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'locationName',
      header: 'Location Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'locationType',
      header: 'Type',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>
          <span className="status-badge active">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'city',
      header: 'City',
      cell: ({ row }) => (
        <div style={{ textAlign: 'left' }}>{row.original.address?.city || '-'}</div>
      ),
    },
    {
      accessorKey: 'country',
      header: 'Country',
      cell: ({ row }) => (
        <div style={{ textAlign: 'left' }}>{row.original.address?.country || '-'}</div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue()?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/assets/location-master/form')
  }

  const handleEdit = (locationData) => {
    navigate('/assets/location-master/edit', { state: { locationData } })
  }

  const handleView = (locationData) => {
    navigate('/assets/location-master/view', { state: { locationData } })
  }

  const handleDelete = (locationData) => {
    setDeleteItem(locationData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting asset location...')
      const response = await assetLocationMasterService.deleteLocation(deleteItem._id)
      if (response.success) {
        toast.success('Asset location deleted successfully!', { autoClose: 2000 })
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete asset location')
      }
    } catch (error) {
      console.error('Error deleting asset location:', error)
      if (error.response?.status === 404) {
        toast.error('Asset location not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this asset location')
      } else if (!error._errorHandled) {
        toast.error('Failed to delete asset location. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Assets', path: '/assets' },
          { label: 'Locations', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Asset Locations</h1>
          <p>Asset locations and storage areas</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Location
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <MapPin size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalLocations || 0}</div>
          <div className="metric-title">Total Locations</div>
          <div className="metric-subtitle">All locations</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeLocations || 0}</div>
          <div className="metric-title">Active Locations</div>
          <div className="metric-subtitle">Currently in use</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <XCircle size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Inactive</span>
            </div>
          </div>
          <div className="metric-value">{stats.inactiveLocations || 0}</div>
          <div className="metric-title">Inactive Locations</div>
          <div className="metric-subtitle">Not in use</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          searchable={true}
          searchPlaceholder="Search locations..."
          noCard={true}
          pageSize={10}
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Asset Location"
        message={`Are you sure you want to delete "${deleteItem?.locationName || deleteItem?.locationCode}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default AssetLocationMaster
